import * as React from 'react';

import {exhaustivenessCheck} from 'nutshell-core/typing/exhaustiveness-check';

import {
    BoxRegularIcon,
    CompetitorRegularIcon,
    ContactIcon,
    CompanyIcon,
    SourcesIcon,
    InvoiceRegularDiagonalIcon,
    QuoteRegularIcon,
    FileIcon,
    ActivityIcon,
    NavIconAutomationIcon,
    PencilToPaperIcon,
    LongTextIcon,
    CalendarIcon,
    NavIconSalesIcon,
    LocationPinIcon,
    LocationPinRegularIcon,
    PhoneIcon,
    PhoneOutlineIcon,
    EmailIcon,
    EnvelopeNewIcon,
    WorldRegularIcon,
    NavIconWebsiteIcon,
    CheckIcon,
    SelectMultiIcon,
    TargetArrowIcon,
    EnvelopeRegularUserIcon,
    DripsRegularIcon,
    StageGoalIcon,
} from 'shells/icon';

import {IconAvatar} from '../avatar/icon-avatar';
import {colors} from '../colors/colors';

import type {RowType} from './types';

type Props = {
    type: RowType;
};

export function RowIcon(props: Props) {
    const getIconInfo = (type: RowType) => {
        switch (type) {
            case 'products':
                return {
                    icon: BoxRegularIcon,
                    color: NutClientConfig.hasRevenueWidgetFF ? colors.lead : colors.product,
                    backgroundColor: NutClientConfig.hasRevenueWidgetFF
                        ? colors.greenLt
                        : colors.brownLt,
                };
            case 'competitors':
                return {
                    icon: CompetitorRegularIcon,
                    color: colors.greyLt,
                    backgroundColor: colors.skeletonGrey,
                };
            case 'contacts':
                return {
                    icon: ContactIcon,
                    color: colors.person,
                    backgroundColor: colors.blueXlt,
                    isLegacyIcon: true,
                };
            case 'accounts':
                return {
                    icon: CompanyIcon,
                    color: colors.company,
                    backgroundColor: colors.plumXlt,
                    isLegacyIcon: true,
                };
            // Kind of non-sensical, since these are always custom
            case 'channels':
                return {
                    icon: SourcesIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                };
            case 'sources':
                return {
                    icon: SourcesIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                };
            case 'invoices':
                return {
                    icon: InvoiceRegularDiagonalIcon,
                    color: colors.lead,
                    backgroundColor: colors.greenLt,
                };
            case 'quotes':
                return {
                    icon: QuoteRegularIcon,
                    color: colors.white,
                    backgroundColor: colors.lead,
                };
            case 'attachments':
                return {
                    icon: FileIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                    isLegacyIcon: true,
                };
            case 'activities':
                return {
                    icon: ActivityIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                    isLegacyIcon: true,
                };
            case 'tasks':
                return {
                    icon: NavIconAutomationIcon,
                    color: colors.orange,
                    backgroundColor: colors.orangeLt,
                    isLegacyIcon: true,
                };
            case 'goals':
                return {
                    icon: TargetArrowIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                    isLegacyIcon: true,
                };
            case 'custom-field-text':
                return {
                    icon: PencilToPaperIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                };
            case 'custom-field-long-text':
                return {
                    icon: LongTextIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                    isLegacyIcon: true,
                };
            case 'custom-field-date':
                return {
                    icon: CalendarIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                    isLegacyIcon: true,
                };
            case 'custom-field-currency':
                return {
                    icon: NavIconSalesIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                    isLegacyIcon: true,
                };
            case 'custom-field-address':
                return {
                    icon: LocationPinIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                    isLegacyIcon: true,
                };
            case 'address':
                return {
                    icon: LocationPinRegularIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                };
            case 'custom-field-phone':
                return {
                    icon: PhoneIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                    isLegacyIcon: true,
                };
            case 'custom-field-email':
                return {
                    icon: EmailIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                    isLegacyIcon: true,
                };
            case 'email':
                return {
                    icon: EnvelopeNewIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                };
            case 'phone':
                return {
                    icon: PhoneOutlineIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                };
            case 'url':
                return {
                    icon: WorldRegularIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                };
            case 'custom-field-url':
                return {
                    icon: NavIconWebsiteIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                    isLegacyIcon: true,
                };
            case 'custom-field-enum-single':
                return {
                    icon: CheckIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                    isLegacyIcon: true,
                };
            case 'custom-field-enum-multi':
                return {
                    icon: SelectMultiIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                    isLegacyIcon: true,
                };
            case 'personal-email-sequence':
                return {
                    icon: EnvelopeRegularUserIcon,
                    color: colors.greyLt,
                    backgroundColor: colors.offWhiteTransparent,
                };
            case 'drip-sequence':
                return {
                    icon: DripsRegularIcon,
                    color: colors.greyLt,
                    backgroundColor: colors.offWhiteTransparent,
                };
            case 'goal':
                return {
                    icon: StageGoalIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                    isLegacyIcon: true,
                };
            default:
                exhaustivenessCheck(type);
        }
    };

    const iconInfo = getIconInfo(props.type);

    return (
        <IconAvatar
            icon={iconInfo.icon}
            avatarSize={32}
            iconSize={iconInfo.isLegacyIcon ? 16 : 20}
            iconColor={iconInfo.color}
            backgroundColor={iconInfo.backgroundColor}
            shape='square'
        />
    );
}
